// src/pages/coach/students/StudentsList.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExcelDownloadButton from '../../../components/common/ExcelDownloadButton';
import { getUserListExportColumns } from '../../../utils/userListExport';
import API from '../../../api';
import PaginationFooter from '../../../components/common/PaginationFooter';
import SortableTableHeader from '../../../components/common/SortableTableHeader';
import ListRecordIdBadge from '../../../components/common/ListRecordIdBadge';
import useSortedClientPagination from '../../../hooks/useSortedClientPagination';
import { userListSortColumns } from '../../../config/tableSortColumns';
import { resolveRowId } from '../../../utils/tableSort';
import UserLink from '../../../components/common/UserLink';
import UserStatusBadge from '../../../components/common/UserStatusBadge';
import ListPageLayout, {
    ListPageHeader,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../../components/layout/ListPageLayout';
import { formatFullName } from '../../../utils/name';
import PlayingPeekCell from '../../playing/PlayingPeekCell';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return v;
}

const StudentsList = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await API.get('/coach/students', { signal: ac.signal });
                const data = Array.isArray(res?.data) ? res.data : [];
                setRows(data);
            } catch (e) {
                setError(t('pages.coachStudents.loadFailed'));
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, []);

    const filteredRows = useMemo(() => {
        const term = (dq || '').trim().toLowerCase();
        if (!term) return rows || [];
        const pick = (x) => (x ?? '').toString().toLowerCase();
        return (rows || []).filter((u) =>
            [
                u.id, u.studentId, u.userId, u.user_id,
                u.firstName, u.middleName, u.lastName,
                u.username || u.userName,
                u.email,
                u.phoneNumber, u.phone,
            ].some((v) => pick(v).includes(term))
        );
    }, [rows, dq]);

    const pagination = useSortedClientPagination(filteredRows, {
        columns: userListSortColumns,
        resetKey: dq,
    });

    const shown = filteredRows.length;
    const total = rows.length;

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.coachStudents.title')}
                subtitle={t('pages.coachStudents.subtitle')}
                actions={(
                    <ExcelDownloadButton
                        columns={getUserListExportColumns(t)}
                        data={filteredRows}
                        filename="students"
                        disabled={filteredRows.length === 0}
                    />
                )}
            />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.coachStudents.searchPlaceholder')}
                shown={shown}
                total={total}
                entityLabel={t('pages.coachStudents.entityLabel')}
            />

            {loading ? (
                <ListLoadingSpinner />
            ) : error ? (
                <div className="text-red-600">{error}</div>
            ) : rows.length === 0 ? (
                <ListEmptyState message={t('pages.coachStudents.empty')} />
            ) : pagination.pageItems.length === 0 ? (
                <ListEmptyState
                    message={t('pages.coachStudents.noMatch')}
                    hint={t('common.emptySearchHint')}
                />
            ) : (
                <>
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.studentName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.username')} columnKey="username" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.email')} columnKey="email" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell text-left font-semibold">
                                        {t('nav.playingPlatform', { defaultValue: 'Playing' })}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((r, idx) => (
                                    <tr
                                        key={String(r.id ?? r.userId ?? r.user_id ?? idx)}
                                        className="table-row"
                                    >
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={resolveRowId(r)} />
                                        </td>
                                        <td className="table-cell">
                                            {formatFullName(r.firstName, r.middleName, r.lastName) ? (
                                                <UserLink
                                                    id={r.id ?? r.userId ?? r.user_id}
                                                    name={formatFullName(r.firstName, r.middleName, r.lastName)}
                                                    playing
                                                />
                                            ) : (
                                                <span className="text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="table-cell">{r.username || r.userName || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{r.email || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">{r.phoneNumber || r.phone || <span className="text-gray-400">-</span>}</td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={r.active} />
                                        </td>
                                        <td className="table-cell">
                                            <PlayingPeekCell userId={r.id ?? r.userId ?? r.user_id} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </ListTableCard>

                <PaginationFooter
                    page={pagination.page}
                    size={pagination.size}
                    total={pagination.total}
                    onPageChange={pagination.setPage}
                    onSizeChange={pagination.setSize}
                />
                </>
            )}
        </ListPageLayout>
    );
};

export default StudentsList;
